/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { mapFetchedDataToPortDetails } from "kontrolEdge/constants/constants";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ProvisioningDetailsModal, { SelectedPortSummary } from "kontrolEdge/components/Modals/ProvisioningDetails";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import RedundancySLAModal from "kontrolEdge/components/Modals/RedundancySLA";
import { ServiceDetailsPageSkeleton } from "kontrolEdge/components/Shared/ServiceDetailsPageSkeleton";
import GenericModal from "@ui-components/Modal/GenericModal";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import StatusTable from "./StatusTable";
import PortConnections from "./PortConnections/PortConnections";
import { usePortDetails } from "./usePortDetails";
import { useAppSelector } from "@redux/hooks";
import { PortPerformanceData } from "@redux/slices/kontrolEdge/portSlice";
import BandwidthChartUniversal from "kontrolEdge/components/Shared/Bandwidth/BandwidthChartUniversal";
import { DetailSummary } from "kontrolEdge/components/Shared/DetailSummary/DetailSummary";
import { toCapacityMbps } from "kontrolEdge/components/Shared/Bandwidth/bandwidthDisplay";
import PortDetailsCard from "./PortDetailsCard";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { apiTagsToSummaries } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";

/* `PortDetailItem` and `PortDetailSection` were declared here as well as in
   `PortDetailsCard.tsx`, and had drifted (CTX-777). They now live only with the
   card that renders them. This page needs neither name: `sections` takes its
   type from `mapFetchedDataToPortDetails`, which is typed against that one
   declaration — which is the point. */

/**
 * First-paint shortcut from the list, never a requirement (CTX-773). Only the customer name
 * is still read from it: the port detail GET returns `tenantId` but not the customer, so a
 * pasted URL shows the Provisioning modal's Customer as "N/A" until the API ships it.
 */
type LocationState = {
  selectedPortRow?: { customer?: { name?: string } };
};

function getLatestInbound(pd?: PortPerformanceData | null) {
  const inbound = pd?.bandwidthUtilization?.inbound;
  if (!inbound?.length) return null;
  return inbound.reduce((prev, curr) =>
    new Date(curr.time).getTime() > new Date(prev.time).getTime() ? curr : prev
  );
}

const PortDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  //Port id selected from MainPort
  const portId = searchParams.get("portId");
  const subid = searchParams.get("subid") ?? undefined;
  const api = useAxiosAuth0();
  const portPerf = useAppSelector((s) => s.port.performanceData);
  const latestInbound = useMemo(() => getLatestInbound(portPerf), [portPerf]);


  const [refresh, setRefresh] = useState(0);
  // Data Layer: fetch core + extras
  const { raw, loading, error } = usePortDetails(
    portId,
    api,
    refresh
  );

  // Resolves from the port id + the detail fetch. The list used to hand the port type over
  // in `location.state`, which a reload or a pasted link loses; the detail GET returns it.
  const location = useLocation();
  const stateCustomerName = (location.state as LocationState | null)?.selectedPortRow?.customer?.name;
  const effectivePortType = ((raw as any)?.portType ??
    (raw as any)?.kontrolEdgePort?.portType) as "physical" | "virtual" | undefined;

  const selectedPortSummary = useMemo<SelectedPortSummary | null>(() => {
    if (!portId) return null;
    const customerName: string | undefined = (raw as any)?.customer?.name ?? stateCustomerName;
    return {
      portId,
      customer: customerName ? { name: customerName } : undefined,
    };
  }, [raw, portId, stateCustomerName]);

    // Derive detail sections
    const sections = useMemo(
      () =>
        raw
          ? mapFetchedDataToPortDetails(raw, undefined, subid)
          : [],
      [raw, subid]
    );

    // Rich tags inline from the detail GET (§5.5). Skips the §5.1 round-trip
    // when the new modal opens. If the response doesn't ship `tags` yet
    // (e.g. KD-563-2 not deployed in this env) the helper returns `[]` and
    // `useResourceTags` falls back to its own fetch.
    const initialAssignedTags = useMemo(
      () => apiTagsToSummaries((raw as { tags?: unknown } | null)?.tags),
      [raw]
    );

  //Edit name and tag inside the component
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [membersModalText, setMembersModalText] = useState("");
  const { tenantId } = useAppSelector((state) => state.selection);

    // Tenant the resource actually lives in (NOT the SideNav lens). Forwarded
    // to the Tags API so partner-view writes (`PUT /resources/.../tags`)
    // hit the right scope — the backend's resource-side adapter is strict on
    // `(id, tenantId)` and doesn't honor `getChildTenantIds`, so passing the
    // partner's own tenant via `useEffectiveTenantId()` 404s when the Port
    // belongs to a child. Always passing the resource tenant fixes both
    // partner and customer view in one go.
    //
    // Falls back to the SideNav lens tenant when `raw.tenantId` isn't yet
    // exposed (loading flicker, or backend regression) so the picker /
    // attach calls always carry an explicit `?tenantId=` and never resolve
    // to the JWT tenant by accident.
    const resourceTenantId = useMemo(() => {
      const t = (raw as { tenantId?: unknown } | null)?.tenantId;
      if (typeof t === "number" && Number.isFinite(t)) return t;
      return tenantId ?? undefined;
    }, [raw, tenantId]);

  // const resourceTenantId = useMemo(
  //   () => tenantId ?? undefined,
  //   [tenantId]
  // );

  const navigate = useNavigate();

  
  // Track the initial tenantId when component mounts to detect changes
  const initialTenantIdRef = useRef<number | null>(null);
  // Initialize the ref on mount
  useEffect(() => {
    if (initialTenantIdRef.current === null) {
      initialTenantIdRef.current = tenantId;
    }
  }, []);
  // Redirect to main view if tenantId changes while on detail page
  useEffect(() => {
    // Skip if ref hasn't been initialized yet
    if (initialTenantIdRef.current === null) return;
    // Only redirect if tenantId actually changed (not on initial mount)
    if (tenantId !== initialTenantIdRef.current) {
      navigate("/kontrol/port");
    }
  }, [tenantId, navigate]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [RedundancyOpen, setRedundancyOpen] = useState(false);

  if (!portId) return <p>No port selected.</p>;
  if (loading && refresh === 0) {
    return <ServiceDetailsPageSkeleton variant="port" />;
  }
  if (error) return <ErrorFetchingItems text={error} />;
  if (!raw) return <p>Port not found.</p>;

  return (
    <>
      {/* Modals */}
      <ProvisioningDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        portDetailsData={sections}
        rawPortData={raw}
        selectedPort={selectedPortSummary}
      />
      <RedundancySLAModal
        isOpen={RedundancyOpen}
        onClose={() => setRedundancyOpen(false)}
        portDetailsData={sections}
        rawPortData={raw}
      />
      <GenericModal
        isOpen={isMembersModalOpen}
        onClose={() => setIsMembersModalOpen(false)}
        title="Members List"
        size="md"
      >
        <p className="text-sm text-semantic-text-secondary">
          {membersModalText}
        </p>
      </GenericModal>

      {/* On a phone the chart follows the summary, above the folded cards (CTX-1421). */}
      <div className="flex flex-col">
      <DetailSummary sections={sections} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
        {sections.map((section, index) => {
          // optional right content per index (keep your existing behavior).
          // Hidden on a phone (CTX-1421): it was cut beside the folded header,
          // the modal repeats what the summary and the card show, and the UTC
          // reading time is the chart's job there.
          const right =
            index === 0 ? (
              <span className="hidden sm:block">
              <GenericButton
                label="Provisioning Details"
                variant="secondary"
                shape="ghost"
                size="sm"
                onClick={() => setIsModalOpen(true)}
              />
              </span>
            ) : index === 1 ? (
              <span className="hidden sm:block">
              <GenericTag
                label={
                  latestInbound
                    ? (() => {
                        const d = new Date(latestInbound.time);
                        const datePart = d.toLocaleDateString("es-MX", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          timeZone: "UTC",
                        });
                        const timePart = d.toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                          timeZone: "UTC",
                        });
                        return `${datePart} ${timePart} UTC`;
                      })()
                    : "-"
                }
              />
              </span>
            ) : null;
          {/*Port detail cards section */}
          return (
            <div key={index} className="flex w-full self-start">
              <PortDetailsCard
                data={{
                  ...section,
                  isCollapsible: section.isCollapsible ?? index >= 2,
                }}
                rightContent={right}
                initialAssignedTags={initialAssignedTags}
                resourceTenantId={resourceTenantId}
              />
            </div>
          );
        })}
      </div>
      {/* Bandwidth Utilization chart */}
      <div className="-order-1 sm:order-none">
      <BandwidthChartUniversal
        service="port"
        id={portId}
        tenantId={tenantId}
        portType={effectivePortType}
        capacityMbps={toCapacityMbps(raw?.capacityMbps)}
      />
      </div>
      </div>
      {/* Associated connections section */}
      <PortConnections portId={portId} />
      {/* Activity Section */}
      <StatusTable
        data={raw?.statusHistory}
        headerContent="Activity"
        foldOnPhone
      />
      {/* {canAccessAlerts && portId && (
        <NotificationsView serviceType="port" serviceId={portId} />
      )} */}
    </>
  );
};

export default PortDetails;
